import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, Loader2 } from "lucide-react";
import { usePostHog } from "@posthog/react";
import api from "@/lib/api";
import { AnswerCard } from "@/components/answers/AnswerCard";
import SearchAutocomplete from "@/components/search/SearchAutocomplete";

export default function Answers() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const question = searchParams.get("q") ?? "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = question.trim()
      ? `${question} — Living Wisdom Explorer`
      : "Search — Living Wisdom Explorer";
    if (!question.trim()) {
      setData(null);
      return;
    }
    let alive = true;
    setLoading(true);
    api
      .get("/kg/search", { params: { q: question.trim() } })
      .then((res) => {
        if (!alive) return;
        setData(res.data);
        posthog?.capture("search_performed", {
          query: question.trim(),
          source: res.data?.source,
          result_count: res.data?.results?.length ?? 0,
        });
      })
      .catch(
        () =>
          alive &&
          setData({ source: "none", message: "Search failed. Please try again." })
      )
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [question, posthog]);

  return (
    <div>
      <div className="sticky top-16 z-20 bg-[#fdfdfb]/90 backdrop-blur-md border-b border-[#14140f]/10">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <SearchAutocomplete
            initialValue={question}
            testIdPrefix="answers-search"
            placeholder="Ask anything…"
            onSubmit={(q) => navigate(`/answers?q=${encodeURIComponent(q)}`)}
            icon={<Search className="w-4 h-4 text-[#14140f]/35 shrink-0" />}
            submitIcon={<Search className="w-4 h-4" />}
            pillClassName="flex items-center gap-2 bg-white rounded-full shadow-sm border border-[#14140f]/10 pl-5 pr-2 py-1.5 focus-within:border-[#ff7360] transition-colors"
            buttonClassName="shrink-0 w-9 h-9 rounded-full bg-[#ff7360] text-[#14140f] flex items-center justify-center hover:bg-[#e85a46] transition-colors"
          />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10">
        {!question.trim() && (
          <p className="text-center text-[#14140f]/45 py-20" data-testid="answers-prompt">
            Ask a question above to search the knowledge graph.
          </p>
        )}
        {loading && (
          <div className="flex items-center justify-center py-24" data-testid="answers-loading">
            <Loader2 className="w-7 h-7 text-[#c23c28] animate-spin" />
          </div>
        )}
        {!loading && data && <AnswerCard question={question} data={data} />}
      </div>
    </div>
  );
}
